// Session-related cookie and header names

/** Cookie name for the main Infinex session */
export const COOKIE_NAME_INFINEX_SESSION = 'infinex_session';

/** Cookie name for the Infinex session key */
export const COOKIE_NAME_INFINEX_SESSION_KEY = 'infinex_session_key';

/** Cookie name for the temporary Infinex session key (used during sign-in flow) */
export const COOKIE_NAME_INFINEX_SESSION_KEY_TEMP = 'infinex_session_key_temp';

/** Header name for passing the Infinex session key */
export const HEADER_NAME_INFINEX_SESSION_KEY = 'x-infinex-session-key';

// Multi-account cookie jar (docs/plans/2026-08-06-001-feat-multi-account-cookie-jar-plan.md)
//
// The active account lives on the bare cookie names above, unchanged. Parked
// accounts store the same sealed payloads under per-account names built from
// these prefixes plus a slot hash (first PARKED_SLOT_HASH_HEX_LENGTH hex chars
// of SHA-256(userId)). The prefixes are deliberately disjoint from the bare
// names so no cookie can be a parsing near-miss of the other family, and short
// because every parked cookie rides every request to the domain.

/** Cookie-name prefix for a parked account's session payload: `inx_ps_<slotHash>` */
export const COOKIE_PREFIX_PARKED_SESSION = 'inx_ps_';

/** Cookie-name prefix for a parked account's session key: `inx_pk_<slotHash>` */
export const COOKIE_PREFIX_PARKED_SESSION_KEY = 'inx_pk_';

/** Hex chars of SHA-256(userId) used as a parked slot name suffix */
export const PARKED_SLOT_HASH_HEX_LENGTH = 8;

/**
 * Maximum accounts in the jar (active + parked); enforced server-side on
 * every path that can add one — `login.parkExisting` and `auth.switchAccount`.
 *
 * The budget is set by the *physical* worst case, not the account count: a
 * switch promotes the target to the bare names but must not delete its parked
 * slot (race rule 2), so the jar transiently holds MAX_JAR_ACCOUNTS + 1 cookie
 * pairs until the picker's self-heal reclaims the duplicate. That transient is
 * what the header-budget measurement in @infinex/session
 * (cookieJarBudget.test.ts) pins: ≈ 6.2 KiB against an 8 KiB Cookie-header
 * budget. Raising this constant without re-measuring will overflow it.
 */
export const MAX_JAR_ACCOUNTS = 4;

/**
 * One account's raw sealed cookie values as read off a request. Values are
 * opaque Iron seals — this type is purely syntactic and implies nothing about
 * validity; unsealing/validation stays with the caller.
 */
export type SessionCookiePair = {
  /** Raw sealed `infinex_session`-shaped payload, if present */
  session?: string;
  /** Raw sealed `infinex_session_key`-shaped payload, if present */
  sessionKey?: string;
};

/**
 * The full multi-account cookie jar parsed from one request. `parked` is keyed
 * by slot hash. `infinex_session_key_temp` is outside the jar by design and
 * never appears here.
 */
export type SessionCookieJar = {
  active: SessionCookiePair;
  parked: Record<string, SessionCookiePair>;
};

/** Header name for passing the transaction/trace ID for distributed tracing */
export const HEADER_NAME_CONNECT_TRANSACTION_ID = 'x-transaction-id';

/**
 * The type of client that is using the session.
 *
 * The extension uses web sessions - the session for both is unified.
 */
export const sessionClientTypes = ['cli', 'web'] as const;
export type SessionClientType = (typeof sessionClientTypes)[number];

export type Session = {
  sessionId: string;
  userId: string;
  passkeyId: string;
  createdAt: number;
  expiresAt: number;
  clientType: SessionClientType;
};

export type SessionToken =
  | SessionHandle
  | { encryptedPayload: string }
  | { jwt: string };

export type SessionHandle = {
  sessionId: string;
  userId?: string;
};

/**
 * Internal session key with sensitive Turnkey API credentials.
 * Contains a compressed P-256 key pair used to stamp Turnkey requests.
 * Server-only — NEVER send to the frontend.
 */
export type SessionKey = {
  /** Turnkey user type - can be used to discriminate operation vs owner session keys */
  turnkeyUserType?: 'operation' | 'owner';
  /** Compressed P-256 key pair for Turnkey API authentication */
  turnkeySessionKey: { publicKey: string; privateKey: string };
  /** ISO timestamp when the session key expires */
  expiry?: string;
  /**
   * ISO timestamp when the key was activated (passkey-stamped Turnkey
   * `createApiKeys`). Travels only inside the server-sealed cookie/header, so
   * clients cannot forge it. Endpoints that require a *fresh* passkey
   * signature (wallet secret export) gate on this; absent on keys minted
   * before the field existed, which such endpoints treat as stale.
   */
  mintedAt?: string;
};

/**
 * Session key stored in cookie with user ID.
 * Contains sensitive credentials — server-only.
 */
export type SessionKeyCookie = SessionKey & {
  userId: string;
};
